import React from 'react';
import { ArrowRight, Heart, Leaf, Bird, TreePine, Sparkles, Star, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { heroContent, siteConfig } from '../data/mock';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const navigate = useNavigate();
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Playful nature background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-sky-50 to-amber-50">
        {/* Animated floating elements */}
        <div className="absolute top-20 left-10 animate-float">
          <Leaf className="w-12 h-12 text-emerald-400 opacity-60" />
        </div>
        <div className="absolute top-32 right-20 animate-float" style={{ animationDelay: '0.5s' }}>
          <Bird className="w-10 h-10 text-sky-500 opacity-50" />
        </div>
        <div className="absolute top-60 left-1/4 animate-float" style={{ animationDelay: '1s' }}>
          <Sparkles className="w-8 h-8 text-amber-400 opacity-60" />
        </div>
        <div className="absolute bottom-40 left-16 animate-float" style={{ animationDelay: '1.5s' }}>
          <TreePine className="w-16 h-16 text-emerald-500 opacity-40" />
        </div>
        <div className="absolute bottom-60 right-1/4 animate-float" style={{ animationDelay: '2s' }}>
          <Star className="w-6 h-6 text-coral-400 opacity-70" fill="currentColor" />
        </div>
        <div className="absolute top-1/2 right-10 animate-float" style={{ animationDelay: '0.8s' }}>
          <Leaf className="w-8 h-8 text-green-500 opacity-50 rotate-45" />
        </div>
        
        {/* Soft gradient blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-200/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-100/30 rounded-full blur-3xl" />
      </div>

      {/* Fun pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5L35 15L45 17L37 25L39 35L30 30L21 35L23 25L15 17L25 15L30 5Z' fill='%2310B981' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-left">
            {/* Playful Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-emerald-200 mb-8 animate-bounce-slow">
              <div className="flex -space-x-1">
                <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
                <Star className="w-4 h-4 text-amber-400" fill="currentColor" />
              </div>
              <span className="text-sm font-medium text-emerald-700">Fun Bible Adventures Await!</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-slate-800 leading-tight mb-6">
              <span className="inline-block hover:scale-105 transition-transform cursor-default">Discover</span>{' '}
              <span className="inline-block text-emerald-600 hover:scale-105 transition-transform cursor-default">Amazing</span>{' '}
              <span className="inline-block hover:scale-105 transition-transform cursor-default">Bible</span>{' '}
              <span className="inline-block text-coral-500 hover:scale-105 transition-transform cursor-default">Stories!</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              {heroContent.subheadline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <Button
                onClick={() => navigate('/login')}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-emerald-500/25 transition-all duration-300 hover:shadow-2xl hover:shadow-emerald-500/30 hover:-translate-y-1 group"
              >
                <BookOpen className="mr-2 w-5 h-5" />
                {heroContent.primaryCTA}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="rounded-full px-8 py-6 text-lg border-2 border-coral-300 text-coral-600 hover:border-coral-400 hover:bg-coral-50 transition-all duration-300 hover:-translate-y-1"
              >
                <Sparkles className="mr-2 w-5 h-5" />
                {heroContent.secondaryCTA}
              </Button>
            </div>

            {/* Scripture Verse with nature decoration */}
            <div className="relative pl-6 border-l-4 border-emerald-400 bg-white/50 backdrop-blur-sm rounded-r-2xl py-4 pr-4">
              <Leaf className="absolute -left-3 -top-2 w-6 h-6 text-emerald-500" />
              <blockquote className="text-slate-600 italic text-lg">
                "{heroContent.verse.text}"
              </blockquote>
              <cite className="text-emerald-600 font-semibold text-sm mt-2 block not-italic">
                — {heroContent.verse.reference}
              </cite>
            </div>
          </div>

          {/* Logo/Visual */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Animated rings */}
              <div className="absolute inset-0 -m-8 bg-gradient-to-br from-emerald-200/50 to-sky-200/50 rounded-full blur-2xl animate-pulse" />
              <div className="absolute inset-0 -m-4 bg-gradient-to-br from-amber-200/30 to-coral-200/30 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }} />
              
              {/* Main logo */}
              <div className="relative bg-white/90 backdrop-blur-sm rounded-full p-8 shadow-2xl border-4 border-white">
                <img
                  src={siteConfig.logo}
                  alt={siteConfig.name}
                  className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 object-contain"
                />
              </div>

              {/* Floating decorative elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDelay: '0.5s' }}>
                <Heart className="w-8 h-8 text-coral-500" fill="currentColor" />
              </div>
              <div className="absolute -bottom-2 -left-6 w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDelay: '1s' }}>
                <Leaf className="w-7 h-7 text-emerald-500" />
              </div>
              <div className="absolute top-1/2 -right-8 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ animationDelay: '1.5s' }}>
                <Star className="w-6 h-6 text-amber-500" fill="currentColor" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator with nature theme */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-sm text-emerald-600 font-medium">Scroll to explore</span>
        <div className="w-8 h-12 border-2 border-emerald-400 rounded-full flex justify-center pt-2">
          <div className="w-2 h-3 bg-emerald-500 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
